import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, BRAND } from "@/data/content";

const PillLink = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group inline-flex items-center gap-2 rounded-full bg-royal px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy"
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-white/70 border-b border-slate-200/60" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-royal font-heading text-lg font-black text-white">AA</span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className={`font-heading text-base font-bold tracking-tight ${scrolled ? "text-navy" : "text-white"}`}>{BRAND.name}</span>
            <span className={`font-body text-[11px] font-medium tracking-wide ${scrolled ? "text-slate-500" : "text-slate-300"}`}>{BRAND.role}</span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <div key={link.label} className="group relative">
                <button
                  data-testid={`nav-${link.label.toLowerCase()}-trigger`}
                  className={`flex items-center gap-1 rounded-full px-4 py-2 font-body text-sm font-medium transition-colors hover:text-royal ${scrolled ? "text-navy" : "text-white"}`}
                >
                  {link.label}
                  <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" strokeWidth={1.5} />
                </button>
                <div className="invisible absolute left-0 top-full w-64 translate-y-2 pt-3 opacity-0 transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-navy/5">
                    {link.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-link-${c.to.replace(/\//g, "-").replace(/^-/, "")}`}
                        className="block rounded-xl px-4 py-2.5 font-body text-sm text-slate-600 transition-colors hover:bg-surface hover:text-royal"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`rounded-full px-4 py-2 font-body text-sm font-medium transition-colors hover:text-royal ${
                  location.pathname === link.to ? "text-royal" : scrolled ? "text-navy" : "text-white"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:block">
          <PillLink to="/book" testId="nav-book-cta">Book Strategy Session</PillLink>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`rounded-full border p-2.5 lg:hidden ${scrolled ? "border-slate-200 bg-white text-navy" : "border-white/20 text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
          >
            <div className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((link) =>
                link.children ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setMobileGroup((g) => (g === link.label ? null : link.label))}
                      className="flex w-full items-center justify-between py-3 font-heading text-lg font-semibold text-navy"
                    >
                      {link.label}
                      <ChevronDown className={`h-5 w-5 transition-transform ${mobileGroup === link.label ? "rotate-180" : ""}`} />
                    </button>
                    {mobileGroup === link.label &&
                      link.children.map((c) => (
                        <Link key={c.to} to={c.to} className="block py-2 pl-4 font-body text-slate-600">
                          {c.label}
                        </Link>
                      ))}
                  </div>
                ) : (
                  <Link key={link.to} to={link.to} className="block py-3 font-heading text-lg font-semibold text-navy">
                    {link.label}
                  </Link>
                )
              )}
              <div className="pt-4">
                <PillLink to="/book" testId="mobile-book-cta">Book Strategy Session</PillLink>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
